import { FieldPath, FieldValues, get, PathValue, useController } from 'react-hook-form';
import { StrictDropdownItemProps } from 'semantic-ui-react';
import { FormSelect, FormSelectProps } from '@britishcouncil/react-admin';

import { RHFormFieldProps } from './types';
import { useFormContext } from './FormContext';
import { ReadonlyFormValue } from './ReadonlyFormValue';

type TValue = string | number | boolean | (string | number | boolean)[] | null;

export type RHFormSelectProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = RHFormFieldProps<FormSelectProps<TValue>, TFieldValues, TName> & {
  onChange?: (value: PathValue<TFieldValues, TName>) => void;
  readonlyMode?: boolean;
  'data-testid'?: string;
  renderReadonlyValue?: (
    value?: PathValue<TFieldValues, TName>,
    option?: StrictDropdownItemProps
  ) => React.ReactNode;
};

export const RHFormSelect = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  name,
  control,
  rules,
  shouldUnregister,
  defaultValue,
  onChange,
  readonlyMode,
  placeholder,
  renderReadonlyValue,
  ...props
}: RHFormSelectProps<TFieldValues, TName>) => {
  const { mode } = useFormContext();
  const { field, formState } = useController({
    name,
    control,
    rules,
    defaultValue,
    shouldUnregister,
  });

  const handleOnChange = (value: PathValue<TFieldValues, TName>) => {
    // null is returned when clearing the select and additional check is needed to not return empty string
    const isEmpty = value === '' || value === undefined;
    field.onChange(!isEmpty ? value : null);
    onChange?.(!isEmpty ? value : (null as PathValue<TFieldValues, TName>));
  };

  if (mode === 'read' || readonlyMode) {
    return (
      <ReadonlyFormValue
        label={props.label}
        data-testid={props['data-testid']}
        value={field.value}
        renderValue={(value) => {
          const option = props.options?.find((opt) => opt.value === value);
          return renderReadonlyValue
            ? renderReadonlyValue(value, option)
            : option
              ? option.text
              : (placeholder ?? '-');
        }}
      />
    );
  }

  return (
    <FormSelect
      {...props}
      placeholder={placeholder ?? 'None selected'}
      name={name}
      value={field.value}
      onChange={handleOnChange}
      onBlur={field.onBlur}
      touched
      error={get(formState.errors, name)?.message}
    />
  );
};
